import type { Simplify, TupleToUnion } from 'type-fest'
import { z } from 'zod'

import type { NonEmptyArray } from './utility-types.ts'

/**
 * Creates a map from enum values to their corresponding IDs
 * @param enumValues array of enum values
 * @param idConstructor function to create an ID from an index
 * @returns a tuple containing the map and a converter function
 * @example
 * const [Values] = EnumBuilder('a', 'b', 'c')
 * const idMap = createEnumMap(Values, MyIdConstructor)
 * // idMap = { a: MyIdConstructor(1), b: MyIdConstructor(2), c: MyIdConstructor(3) }
 */
export function createEnumMap<
  T extends string,
  U extends (id: number) => unknown,
>(
  enumValues: T[],
  idConstructor: U
): [Record<T, ReturnType<U>>, (value: number) => T] {
  const map = enumValues.reduce(
    (acc, value, index) =>
      Object.assign(acc, { [value]: idConstructor(index + 1) }),
    {} as Record<T, ReturnType<U>>
  )

  const converter = (value: number): T => {
    if (value < 1 || value > enumValues.length) {
      throw new Error(`Invalid enum value: ${value}`)
    }

    const result = enumValues[value - 1]
    if (!result) {
      throw new Error(`Invalid enum value: ${value}`)
    }

    return result
  }

  return [map, converter]
}

/**
 * @param values the list of values to create an enum from
 * @returns a tuple containing the enum values, the zod schema for validating the enum, and a map object for all the enum values
 */
export const EnumBuilder = <
  Items extends string,
  Values extends Readonly<NonEmptyArray<Items>>,
>(
  ...values: Values
) => {
  const schema = z.enum(values)

  const map = values.reduce(
    (map, value) => Object.assign(map, { [value]: value }),
    {} as Simplify<Readonly<{ [Value in TupleToUnion<Values>]: Value }>>
  )

  return Object.assign([values, schema, map] as const, {
    default: <Value extends TupleToUnion<Values>>(value: Value) =>
      [values, schema.prefault(value), map] as const,
    catch: <Value extends TupleToUnion<Values>>(value: Value) =>
      [values, schema.catch(value), map] as const,
  })
}

/**
 * Strips the Zod brand from a branded string type, recovering the
 * original string literal union.  Use this in template-literal i18n
 * keys so TypeScript can resolve the key to the correct union.
 */
export type Unbrand<T, Brand extends string> = T extends z.BRAND<Brand> &
  infer R
  ? R
  : T

/**
 * Creates a nominal (branded) enum with Zod validation and a type-safe
 * unbrand function for recovering the original string literals.
 *
 * Like {@link EnumBuilder}, but applies a Zod `.brand<Brand>()` so that
 * values from different enums with overlapping members (e.g. `'meta'` in
 * both `PackageSource` and `PartnerProgramKind`) are not interchangeable
 * at the type level.
 *
 * @param _brand - Object whose `brand` property names the brand tag
 * @param values - One or more string literal values for the enum
 * @returns A tuple of `[brandedValues, schema, enumMap, unbrand]` with
 *   `.default()` and `.catch()` variants:
 *   - `brandedValues` — readonly array of branded enum members
 *   - `schema` — Zod schema that parses a string into a branded value
 *   - `enumMap` — frozen object keyed by each member for dot-access
 *   - `unbrand` — strips the brand, returning the original string literal
 *     union so it can be used in template-literal i18n keys, object lookups,
 *     and other contexts where TypeScript needs the plain string
 *
 * @example
 * const [ChargeMethodValues, ChargeMethodSchema, ChargeMethodEnum, unbrandChargeMethod] =
 *   CreateBrandedEnum({ brand: 'ChargeMethod' }, 'stripe', 'invoice', 'ams')
 *
 * type ChargeMethod = z.infer<typeof ChargeMethodSchema>
 * //   ^? ("stripe" | "invoice" | "ams") & z.BRAND<"ChargeMethod">
 *
 * // Dot-access with full type safety
 * const method = ChargeMethodEnum.stripe
 *
 * // Unbrand for i18n template literals
 * t(`billing.${unbrandChargeMethod(method)}`)
 * //           ^? "stripe" — resolves to a valid i18n key
 */
export const CreateBrandedEnum = <
  Brand extends string,
  Items extends string,
  Values extends Readonly<NonEmptyArray<Items>>,
>(
  _brand: { brand: Brand },
  ...values: Values
) => {
  const baseSchema = z.enum(values)
  const schema = baseSchema.brand<Brand>()

  type BrandedItem<T> = T extends T ? T & z.BRAND<Brand> : never
  type Branded = BrandedItem<TupleToUnion<Values>>
  type BrandedValues = Readonly<NonEmptyArray<Branded>>
  type BrandedMap = Simplify<
    Readonly<{ [Value in TupleToUnion<Values>]: Value & z.BRAND<Brand> }>
  >

  const brandedValues = values as unknown as BrandedValues
  const map = values.reduce(
    (map, value) => Object.assign(map, { [value]: value }),
    {} as BrandedMap
  )

  const unbrand = <T extends Branded>(value: T): Unbrand<T, Brand> =>
    value as Unbrand<T, Brand>

  return Object.assign([brandedValues, schema, map, unbrand] as const, {
    default: <Value extends TupleToUnion<Values>>(value: Value) =>
      [brandedValues, schema.prefault(value), map, unbrand] as const,
    catch: <Value extends TupleToUnion<Values>>(value: Value) =>
      [
        brandedValues,
        baseSchema.catch(value).brand<Brand>(),
        map,
        unbrand,
      ] as const,
  })
}
